/**
 * On-demand box-detail API (scan + photos) — talks to mttr/app_api. Loaded only
 * when the box detail page opens, not part of the every-refresh snapshot.
 */

import { ApiError, apiFetch } from "./apiClient";
import type { Box } from "../types/boxes";

export interface AntennaCount {
  antenna_number: number;
  epc_count: number;
}

/** One reader's ports on a scan. Port numbers are per READER: reader 1 port 4
 *  and reader 2 port 4 are different physical antennas at opposite ends of the
 *  box, so they are never merged. */
export interface ReaderAntennas {
  reader_id: number;
  antennas: AntennaCount[];
}

export interface BoxScanDetail {
  scu_name: string;
  scan_id: number | null;
  scan_end_utc: string | null;
  total_tags: number | null;
  /** Platform-only. Kept for older clients: this is the FIRST reader's ports,
   *  never a merge. Build against `readers`. */
  antennas?: AntennaCount[];
  /** Platform-only. One entry per reader that read anything. */
  readers?: ReaderAntennas[];
}

/** `readers` when the server sent it, else the flat list as a single reader.
 *  One place to do the fallback, so every antenna view agrees. */
export function readerGroups(scan: BoxScanDetail | null): ReaderAntennas[] {
  if (!scan) return [];
  if (scan.readers && scan.readers.length > 0) return scan.readers;
  if (scan.antennas && scan.antennas.length > 0) {
    return [{ reader_id: 1, antennas: scan.antennas }];
  }
  return [];
}

export interface BoxPhoto {
  azure_url: string;
  timestamp: string | null;
  camera_name: string | null;
}

export interface BoxPhotos {
  mode: "recent" | "range";
  total: number;
  limit: number;
  offset: number;
  from?: string;
  to?: string;
  /** Whether the result was filtered to AI-flagged-person photos. Echoed
   *  back by the server so the UI can reflect the active filter. */
  only_person?: boolean;
  photos: BoxPhoto[];
}

export const PHOTO_PAGE_SIZES = [6, 12, 24, 48] as const;

export interface BoxEvent {
  camera_person_episode_id: number;
  start_time: string | null;
  end_time: string | null;
  duration_seconds: number | null;
  max_persons: number | null;
  total_photos: number | null;
  photos_with_person: number | null;
  photos: BoxPhoto[];
}

export interface BoxEvents {
  total: number;
  limit: number;
  offset: number;
  from?: string | null;
  to?: string | null;
  events: BoxEvent[];
}

export const EVENT_PAGE_SIZES = [4, 8, 16, 30] as const;

/** Manual maintenance-ticket categories (the "report an issue" flow). */
export const ISSUE_CATEGORIES: { key: string; label: string }[] = [
  { key: "lights", label: "Lights need repair" },
  { key: "starlink", label: "Starlink broken / offline" },
  { key: "electrical", label: "Electrical box needs repair" },
  { key: "camera", label: "Camera quality / alignment problem" },
  { key: "other", label: "Other issue" },
];

export class BoxDetailApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "BoxDetailApiError";
  }
}

async function getJson<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new BoxDetailApiError(e.message, e.status) : e;
  }
}

export function fetchScanDetail(scu: string, token: string): Promise<BoxScanDetail> {
  return getJson<BoxScanDetail>(`/api/boxes/${encodeURIComponent(scu)}/scan`, token);
}

/** One per-smartbox freeform note (unique install / special feature). */
export interface BoxNote {
  note_id: number;
  category: string | null;
  note: string;
  is_pinned: boolean;
  created_by: string | null;
  created_utc: string | null;
  updated_utc: string | null;
}

export interface BoxNotes {
  scu_name: string;
  notes: BoxNote[];
}

/** Per-smartbox notes for the box-detail card. PLATFORM-ONLY — the server
 *  returns 404 for non-platform callers, so callers should only invoke this
 *  when the logged-in role is platform. */
export function fetchBoxNotes(scu: string, token: string): Promise<BoxNotes> {
  return getJson<BoxNotes>(`/api/boxes/${encodeURIComponent(scu)}/notes`, token);
}

/** Editing notes requires operator.triage (checked server-side). */
export interface BoxNoteInput {
  note?: string;
  /** null clears the category; omit to leave unchanged (on update). */
  category?: string | null;
  is_pinned?: boolean;
}

export function createBoxNote(
  scu: string,
  body: BoxNoteInput,
  token: string,
): Promise<{ note_id: number }> {
  return getJson<{ note_id: number }>(
    `/api/boxes/${encodeURIComponent(scu)}/notes`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function updateBoxNote(
  scu: string,
  noteId: number,
  body: BoxNoteInput,
  token: string,
): Promise<{ note_id: number }> {
  return getJson<{ note_id: number }>(
    `/api/boxes/${encodeURIComponent(scu)}/notes/${noteId}`,
    token,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export function deleteBoxNote(
  scu: string,
  noteId: number,
  token: string,
): Promise<{ deleted: boolean }> {
  return getJson<{ deleted: boolean }>(
    `/api/boxes/${encodeURIComponent(scu)}/notes/${noteId}`,
    token,
    { method: "DELETE" },
  );
}

/** Fetch a SINGLE box's snapshot object — same per-box shape as one element of
 *  the fleet list, scoped server-side. Detail pages use this instead of
 *  pulling the whole fleet just to `.find()` one box. Rejects (404) when the
 *  SCU isn't in the caller's scope. */
export async function fetchBox(scu: string, token: string): Promise<Box> {
  const { box } = await getJson<{ box: Box }>(
    `/api/boxes/${encodeURIComponent(scu)}`,
    token,
  );
  return box;
}

export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";

export interface BoxTicketHistoryEntry {
  ticket_origin: "source" | "_jcox";
  ticket_id: number;
  // The ticket's OWN SCU — may differ from the box's current SCU when it was
  // opened on hardware since swapped out (tickets follow the smartbox).
  scu_name: string;
  status: TicketStatus | string;
  issue_type: string | null;
  priority: string | null;
  summary: string | null;
  description: string | null;
  created_by: string | null;
  date_created: string | null;
  date_resolved: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  /** Set when the source ticket has an MTTR-side overlay marking it
   *  resolved (source row stays open in the source DB). */
  mttr_resolved_utc: string | null;
  mttr_resolved_by: string | null;
}

export interface BoxTicketHistory {
  scu_name: string;
  tickets: BoxTicketHistoryEntry[];
}

export function fetchBoxTickets(scu: string, token: string): Promise<BoxTicketHistory> {
  return getJson<BoxTicketHistory>(
    `/api/boxes/${encodeURIComponent(scu)}/tickets`,
    token,
  );
}

/** No opts -> the 2 most recent photos. With from/to -> a paged date-range
 *  window (server caps it to one month). `onlyPerson=true` filters to photos
 *  whose AI analysis flagged a person (drops both ai-said-no and unanalyzed
 *  rows). */
export function fetchPhotos(
  scu: string,
  token: string,
  opts: {
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    onlyPerson?: boolean;
  } = {},
): Promise<BoxPhotos> {
  const p = new URLSearchParams();
  if (opts.from) p.set("from", opts.from);
  if (opts.to) p.set("to", opts.to);
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset != null) p.set("offset", String(opts.offset));
  if (opts.onlyPerson) p.set("person", "1");
  const qs = p.toString();
  return getJson<BoxPhotos>(
    `/api/boxes/${encodeURIComponent(scu)}/photos${qs ? `?${qs}` : ""}`,
    token,
  );
}

/** Person-episode events with their AI-confirmed photos. No opts -> the
 *  ~8 most recent events with photos; with from/to -> a paged date-range
 *  window (server caps to one month). Each event is one row in
 *  `vision_person_episode` (photos_with_person > 0). */
export function fetchEvents(
  scu: string,
  token: string,
  opts: { from?: string; to?: string; limit?: number; offset?: number } = {},
): Promise<BoxEvents> {
  const p = new URLSearchParams();
  if (opts.from) p.set("from", opts.from);
  if (opts.to) p.set("to", opts.to);
  if (opts.limit != null) p.set("limit", String(opts.limit));
  if (opts.offset != null) p.set("offset", String(opts.offset));
  const qs = p.toString();
  return getJson<BoxEvents>(
    `/api/boxes/${encodeURIComponent(scu)}/events${qs ? `?${qs}` : ""}`,
    token,
  );
}

/** Open a manual maintenance ticket for a box. Creator is set server-side from
 *  the login. */
export async function reportIssue(
  scu: string,
  body: { category: string; description?: string; priority?: "low" | "normal" | "high" },
  token: string,
): Promise<{ ticket_id: number }> {
  return getJson<{ ticket_id: number }>(
    `/api/boxes/${encodeURIComponent(scu)}/report`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** What a reboot request answers. `rebooted` true means the reboot was
 *  SCHEDULED, not that the box is back — it will not be, for a minute or
 *  more, and `expect_back_after_seconds` is how long to expect. */
export interface RebootResult {
  scu_name: string;
  rebooted: boolean;
  hardware: string;
  note: string;
  expect_back_after_seconds: number;
}

/**
 * Reboot the SCU over SSH — the compute unit only.
 *
 * There is no way to power-cycle the SmartBox itself from here, so nothing in
 * this path should be labelled as restarting "the box".
 *
 * `confirm` is required by the server, not just the UI — an accidental POST
 * must not restart a rig's box. Needs `operator.triage` AND write scope on the
 * SCU: being able to see a box is not permission to restart it.
 */
export function rebootBox(scu: string, token: string): Promise<RebootResult> {
  return apiFetch<RebootResult>(
    `/api/boxes/${encodeURIComponent(scu)}/reboot`,
    token,
    { method: "POST", body: JSON.stringify({ confirm: true }) },
  );
}
